/**
 * SkipCash Payment Integration Service
 * 
 * IMPORTANT: This service calls backend endpoints. You MUST create backend API endpoints
 * to handle payment creation securely (key secret must never be exposed to frontend).
 * 
 * Backend endpoints needed:
 * - POST /api/skipcash/create-payment - Creates payment and returns payUrl
 * - GET /api/skipcash/payment-status/:id - Gets payment status
 * - POST /api/skipcash/webhook - Handles webhooks from SkipCash
 */

// Configuration - Update these with your SkipCash credentials
// For production, these should come from environment variables
const SKIPCASH_CONFIG = {
  // Test environment
  test: {
    baseUrl: 'https://skipcashtest.azurewebsites.net',
    // These should be stored securely on backend, not here
  },
  // Production environment
  production: {
    baseUrl: 'https://api.skipcash.app',
  },
};

// Use test environment by default
const isProduction = process.env.REACT_APP_ENV === 'production';
const config = isProduction ? SKIPCASH_CONFIG.production : SKIPCASH_CONFIG.test;

/**
 * Create a SkipCash payment
 * This should call your backend endpoint which creates the payment securely
 * 
 * @param {Object} paymentData - Payment data
 * @param {string} paymentData.amount - Amount as string (e.g., "15.25")
 * @param {string} paymentData.firstName - Customer first name
 * @param {string} paymentData.lastName - Customer last name
 * @param {string} paymentData.email - Customer email
 * @param {string} paymentData.phone - Customer phone
 * @param {string} paymentData.street - Street address
 * @param {string} paymentData.city - City
 * @param {string} paymentData.state - State (ISO code, max 2 chars)
 * @param {string} paymentData.country - Country (ISO code, max 2 chars, e.g., "QA")
 * @param {string} paymentData.postalCode - Postal code
 * @param {string} paymentData.transactionId - Your internal order ID
 * @param {string} paymentData.custom1 - Optional custom field
 * @returns {Promise<Object>} Payment response with payUrl
 */
export const createPayment = async (paymentData) => {
  try {
    // Call Firebase Cloud Function
    // Replace with your actual Firebase Function URL after deployment
    const functionUrl = process.env.REACT_APP_SKIPCASH_CREATE_PAYMENT_URL || 
      'https://us-central1-abaya-12404.cloudfunctions.net/createSkipCashPayment';
    
    console.log('📤 Sending request to:', functionUrl);
    console.log('📦 Request payload:', JSON.stringify(paymentData, null, 2));
    
    const requestStartTime = Date.now();
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(paymentData),
    });

    const requestDuration = Date.now() - requestStartTime;
    console.log(`⏱️ Request completed in ${requestDuration}ms`);
    console.log('📊 Response status:', response.status);
    
    // Get response text first to handle both JSON and non-JSON errors
    const responseText = await response.text();
    console.log('📦 Response text length:', responseText.length);
    console.log('📦 Response text preview:', responseText.substring(0, 500));
    
    if (!response.ok) {
      let errorData;
      try {
        errorData = JSON.parse(responseText);
        console.error('❌ Error response (JSON):', JSON.stringify(errorData, null, 2));
      } catch (e) {
        // If response is not JSON, create error object from text
        console.error('❌ Error response (text):', responseText);
        errorData = {
          message: responseText || `HTTP error! status: ${response.status}`,
          error: 'Server error',
          details: responseText.substring(0, 500),
        };
      }
      
      // Create error with details
      const errorMessage = errorData.message || 
                          errorData.error || 
                          errorData.errorMessage ||
                          `HTTP error! status: ${response.status}`;
      
      const error = new Error(errorMessage);
      error.details = errorData;
      error.statusCode = response.status;
      error.responseText = responseText.substring(0, 1000); // Limit length
      throw error;
    }

    // Parse JSON response
    let data;
    try {
      data = JSON.parse(responseText);
      console.log('✅ Success response:', JSON.stringify(data, null, 2));
    } catch (e) {
      console.error('❌ Invalid JSON response:', responseText.substring(0, 500));
      throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}`);
    }
    
    return data;
  } catch (error) {
    console.error('❌ Error creating SkipCash payment:', error);
    console.error('Error name:', error.name);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    throw error;
  }
};

/**
 * Get payment status
 * 
 * @param {string} paymentId - SkipCash payment ID
 * @returns {Promise<Object>} Payment status
 */
export const getPaymentStatus = async (paymentId) => {
  try {
    // Call Firebase Cloud Function
    // The URL will be set after deploying Firebase Functions
    const functionUrl = process.env.REACT_APP_SKIPCASH_STATUS_URL || 
      'https://us-central1-abaya-12404.cloudfunctions.net/getSkipCashPaymentStatus';
    
    const response = await fetch(`${functionUrl}?id=${paymentId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get payment status');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting payment status:', error);
    throw error;
  }
};

/**
 * Confirm SkipCash payment and create order on backend if paid
 *
 * @param {Object} payload
 * @param {string} payload.receiptToken
 * @param {string} payload.orderId
 * @param {string} payload.paymentId
 * @returns {Promise<Object>} Confirmation response
 */
export const confirmSkipCashOrder = async ({ receiptToken, orderId, paymentId }) => {
  const functionUrl = process.env.REACT_APP_SKIPCASH_CONFIRM_URL ||
    'https://us-central1-abaya-12404.cloudfunctions.net/confirmSkipCashOrder';

  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      receiptToken,
      orderId,
      paymentId,
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    const message = data?.message || data?.error || 'Failed to confirm order';
    throw new Error(message);
  }

  return data;
};

/**
 * Get order receipt data by receipt token
 *
 * @param {string} receiptToken
 * @returns {Promise<Object>} Receipt response
 */
export const getOrderReceipt = async (receiptToken) => {
  const functionUrl = process.env.REACT_APP_RECEIPT_URL ||
    'https://us-central1-abaya-12404.cloudfunctions.net/getOrderReceipt';

  const response = await fetch(`${functionUrl}?token=${encodeURIComponent(receiptToken)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const data = await response.json();
  if (!response.ok) {
    const message = data?.message || data?.error || 'Failed to load receipt';
    throw new Error(message);
  }

  return data;
};

/**
 * Open SkipCash payment in popup modal
 * This function returns the payUrl to be used in a modal component
 * The modal component should handle the iframe and payment completion
 * 
 * @param {string} payUrl - Payment URL from SkipCash
 * @param {Function} onComplete - Callback when payment completes
 * @param {Function} onClose - Callback when payment is closed/canceled
 * @returns {Object} Object with payUrl and handlers
 */
export const openPaymentModal = (payUrl, onComplete, onClose) => {
  // Return the payUrl and handlers for the modal component to use
  return {
    payUrl,
    onComplete,
    onClose,
  };
};

/**
 * Process SkipCash payment flow - Returns payment modal data
 * 
 * @param {Object} orderData - Order data
 * @param {Function} onSuccess - Success callback
 * @param {Function} onError - Error callback
 * @returns {Promise<Object>} Payment modal configuration
 */
export const processSkipCashPayment = async (orderData, onSuccess, onError) => {
  try {
    // Create payment
    const paymentResponse = await createPayment(orderData);
    
    if (!paymentResponse.resultObj || !paymentResponse.resultObj.payUrl) {
      throw new Error('Invalid payment response');
    }

    const { id: paymentId, payUrl } = paymentResponse.resultObj;

    // Return modal configuration
    return {
      payUrl,
      paymentId,
      onSuccess,
      onError,
    };
  } catch (error) {
    onError(error);
    return null;
  }
};

/**
 * Check payment status and handle completion
 * 
 * @param {string} paymentId - SkipCash payment ID
 * @param {Function} onSuccess - Success callback
 * @param {Function} onError - Error callback
 */
export const checkPaymentStatus = async (paymentId, onSuccess, onError) => {
  try {
    // Wait a moment for webhook to process (if payment completed)
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Check payment status
    const statusResponse = await getPaymentStatus(paymentId);
    
    if (statusResponse.resultObj) {
      const status = statusResponse.resultObj.statusId;
      // Status: 0=new, 1=pending, 2=paid, 3=canceled, 4=failed, 5=rejected, 6=refunded
      if (status === 2) {
        // Payment successful
        onSuccess(statusResponse.resultObj);
      } else if (status === 3) {
        // Payment canceled
        onError(new Error('Payment was canceled'));
      } else if (status === 4 || status === 5) {
        // Payment failed or rejected
        onError(new Error(`Payment ${statusResponse.resultObj.status}`));
      } else {
        // Still pending or new - might complete later via webhook
        onError(new Error('Payment is still processing. You will be notified when payment is confirmed.'));
      }
    } else {
      onError(new Error('Unable to verify payment status'));
    }
  } catch (error) {
    onError(error);
  }
};

export default {
  createPayment,
  getPaymentStatus,
  openPaymentModal,
  processSkipCashPayment,
  checkPaymentStatus,
};
